import { Link } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import {
  H1,
  Button,
  ChevronLeftIcon,
  Input,
  LoadingDots,
  SaveIcon,
  Text,
  RadioInput,
} from '@/components/atoms'
import { SelectField } from '@/components/molecules'
import { usePaymentOutUpdatePage } from './page.payment-out-update.hook'
import { ROUTES } from '@/shared/constants/routes'
import { PaymentPage } from '../page.payment'

interface PaymentOutUpdatePageProps {
  onClose?: () => void
  onSuccess?: () => void
}

export function PaymentLayoutWithPayOut() {
  return <PaymentPage initialModal="payOut" />
}

export function PaymentOutUpdatePage({
  onClose,
  onSuccess,
}: PaymentOutUpdatePageProps = {}) {
  const { t } = useTranslation()
  const {
    isOffline,
    accountHolderName,
    setAccountHolderName,
    bankName,
    setBankName,
    banks,
    accountNumber,
    handleAccountNumberChange,
    routingNumber,
    handleRoutingNumberChange,
    accountType,
    setAccountType,
    accountTypes,
    handleBlur,
    errors,
    isLoading,
    isSaving,
    saveError,
    handleSave,
  } = usePaymentOutUpdatePage({ onSuccess })

  return (
    <div className="flex flex-col">
      <div className="px-8 pb-4">
        {!onClose && (
          <Link
            to={ROUTES.PAYMENT}
            className="flex items-center gap-1 text-sm text-neutral-600"
          >
            <ChevronLeftIcon />
            {t('payment.back')}
          </Link>
        )}

        <div className="flex flex-col gap-2">
          <Text className="!text-base/[1.2rem] font-bold text-neutral-600 uppercase">
            {t('payment.updatePayOut.subtitle')}
          </Text>
          <H1
            data-testid="pay-out-title"
            className="font-600 !text-[1.5rem]/[120%] text-indigo-900"
          >
            {t('payment.updatePayOut.title')}
          </H1>
        </div>

        {isLoading ? (
          <div className="flex justify-center py-8">
            <LoadingDots />
          </div>
        ) : (
          <div className="mx-auto mt-8 w-full max-w-[900px] space-y-4">
            <div className="form-control flex w-full flex-col gap-2">
              <span className="label-text text-xs text-neutral-600">
                {t('payment.updatePayOut.accountType')}
              </span>
              <div className="flex gap-6 md:gap-[4rem]">
                {accountTypes.map((type) => (
                  <RadioInput
                    key={type}
                    name="accountType"
                    value={type}
                    checked={accountType === type}
                    onChange={() => setAccountType(type)}
                    label={t(`payment.updatePayOut.accountTypes.${type}`)}
                    data-testid={`account-type-${type}`}
                  />
                ))}
              </div>
            </div>

            <Input
              id="accountHolderName"
              data-testid="account-holder-name-input"
              label={t('payment.updatePayOut.accountHolderName')}
              value={accountHolderName}
              onChange={(e) => setAccountHolderName(e.target.value)}
              onBlur={() => handleBlur('accountHolderName')}
              placeholder={t(
                'payment.updatePayOut.accountHolderNamePlaceholder',
              )}
              error={
                errors.accountHolderName
                  ? t(`payment.updatePayOut.${errors.accountHolderName}`)
                  : undefined
              }
            />

            <SelectField
              id="bankName"
              data-testid="bank-name-select"
              searchable
              label={t('payment.updatePayOut.bankName')}
              value={bankName}
              options={banks.map((bank) => ({
                value: bank.name ?? '',
                label: bank.name ?? '',
              }))}
              placeholder={t('payment.updatePayOut.bankNamePlaceholder')}
              searchPlaceholder={t('payment.updatePayOut.bankNamePlaceholder')}
              error={
                errors.bankName
                  ? t(`payment.updatePayOut.${errors.bankName}`)
                  : undefined
              }
              onSelect={setBankName}
              onBlur={() => handleBlur('bankName')}
            />

            <div className="flex gap-4 md:gap-6">
              <div className="flex-1">
                <Input
                  id="routingNumber"
                  data-testid="routing-number-input"
                  label={t('payment.updatePayOut.routingNumber')}
                  value={routingNumber}
                  onChange={(e) => handleRoutingNumberChange(e.target.value)}
                  onBlur={() => handleBlur('routingNumber')}
                  placeholder="123456789"
                  error={
                    errors.routingNumber
                      ? t(`payment.updatePayOut.${errors.routingNumber}`)
                      : undefined
                  }
                  inputMode="numeric"
                />
              </div>
              <div className="flex-1">
                <Input
                  id="accountNumber"
                  data-testid="account-number-input"
                  label={t('payment.updatePayOut.accountNumber')}
                  value={accountNumber}
                  onChange={(e) => handleAccountNumberChange(e.target.value)}
                  onBlur={() => handleBlur('accountNumber')}
                  placeholder="••••••••••"
                  error={
                    errors.accountNumber
                      ? t(`payment.updatePayOut.${errors.accountNumber}`)
                      : undefined
                  }
                  inputMode="numeric"
                />
              </div>
            </div>

            {saveError && (
              <div role="alert" className="alert alert-error">
                <span>{t('payment.updatePayOut.error')}</span>
              </div>
            )}

            {isOffline && (
              <Text className="text-indigo-base !text-sm">
                {t('payment.offlineError')}
              </Text>
            )}

            <div className="flex flex-wrap justify-center gap-3 pt-[1.62rem]">
              <Button
                onClick={handleSave}
                loading={isSaving}
                disabled={isSaving || isOffline}
                data-testid="save-button"
                className="btn-lg w-full !rounded-lg !text-base md:!w-full"
              >
                {t('payment.updatePayOut.save')}
                <SaveIcon className="mr-2 h-6 w-6" />
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
